import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../styles/ui.css";
import "./ReviewProposal.css";
import BackButton from "../../components/BackButton";
import useSidebarWidthVar from "../../hooks/useSidebarWidthVar";

const normalize = (s) => String(s || "").toLowerCase().trim();

const ReviewProposal = () => {
  useSidebarWidthVar();

  const { proposalId, custom_id } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [proposalTitle, setProposalTitle] = useState("");
  const [proposalCode, setProposalCode] = useState(custom_id || "");
  const [loading, setLoading] = useState(true);
  const [openDocPath, setOpenDocPath] = useState(null);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";

  useEffect(() => {
    const fetchProposalDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/proposals/details/${proposalId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        const data = await response.json();

        if (response.ok && Array.isArray(data.documents)) {
          setDocuments(data.documents);
          if (data.proposal?.title) setProposalTitle(data.proposal.title);
          if (data.proposal?.custom_id) setProposalCode(data.proposal.custom_id);

          const initial = {};
          data.documents.forEach((doc) => {
            initial[doc.document_id] = {
              status: doc.status || "accepted",
              comment: doc.comments || "",
            };
          });
          setFeedback(initial);
        } else {
          console.error("Failed to load proposal documents");
        }
      } catch (err) {
        console.error("Error fetching proposal documents:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProposalDetails();
  }, [proposalId, API_BASE_URL]);

  const handleStatusChange = (docId, status) =>
    setFeedback((prev) => ({ ...prev, [docId]: { ...prev[docId], status } }));

  const handleCommentChange = (docId, comment) =>
    setFeedback((prev) => ({ ...prev, [docId]: { ...prev[docId], comment } }));

  const handleSubmitReview = async (finalStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/review/${proposalId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ feedback, finalStatus }),
      });

      const result = await response.json();
      alert(result.message || "Review saved.");
      navigate("/proposal/view-submissions");
    } catch (error) {
      console.error("Error saving review:", error);
      alert("Failed to save review.");
    }
  };

  const withToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token || !url.includes('/api/files/')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  const openModal = (docPath) => setOpenDocPath(withToken(`${API_BASE_URL}${docPath}`));
  const closeModal = () => setOpenDocPath(null);

  const allAccepted = Object.values(feedback).every((f) => f.status === "accepted");
  const anyResubmit = Object.values(feedback).some((f) => f.status === "resubmit");

  if (loading) return <div className="page-loading">Loading…</div>;

  return (
    <div className="review-proposal-container wide">
      <div className="card head-card">
        <div className="rp-header">
          <h2 className="page-title">Review Proposal</h2>
          <div className="id-badge" title="Proposal ID">{proposalCode || "—"}</div>
        </div>
        <div className="subtitle">{proposalTitle || "Untitled Proposal"}</div>
      </div>

      <div className="card">
        <table className="table-std review-table dense">
          <thead>
            <tr>
              <th>#</th>
              <th>Document Type</th>
              <th>Preview</th>
              <th>Status</th>
              <th>Comment (if resubmit)</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, index) => (
              <tr key={doc.document_id}>
                <td>{index + 1}</td>
                <td>{doc.document_name}</td>
                <td className="button-cell">
                  <button className="btn xs" onClick={() => openModal(doc.path)}>
                    View
                  </button>
                </td>
                <td>
                  <div className="current-status">
                    Current:&nbsp;<strong>{feedback[doc.document_id]?.status}</strong>
                  </div>
                  <div className="segmented-toggle">
                    <button
                      className={`accepted ${
                        feedback[doc.document_id]?.status === "accepted" ? "selected" : ""
                      }`}
                      onClick={() => handleStatusChange(doc.document_id, "accepted")}
                    >
                      Accept
                    </button>
                    <button
                      className={`resubmit ${
                        feedback[doc.document_id]?.status === "resubmit" ? "selected" : ""
                      }`}
                      onClick={() => handleStatusChange(doc.document_id, "resubmit")}
                    >
                      Resubmit
                    </button>
                  </div>
                </td>
                <td>
                  {feedback[doc.document_id]?.status === "resubmit" && (
                    <textarea
                      rows="2"
                      className="textarea-std"
                      value={feedback[doc.document_id]?.comment}
                      onChange={(e) =>
                        handleCommentChange(doc.document_id, e.target.value)
                      }
                      placeholder="Enter comment…"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="btn-row nowrap actions-row">
          <button
            className="btn primary"
            disabled={!allAccepted}
            onClick={() => handleSubmitReview("Accept Submission for Review")}
            title={allAccepted ? "Accept submission" : "All docs must be accepted"}
          >
            Accept Submission for Review
          </button>

          <button
            className="btn warn"
            disabled={!anyResubmit}
            onClick={() => handleSubmitReview("Request Resubmission")}
            title={anyResubmit ? "Request resubmission" : "Mark at least one doc as Resubmit"}
          >
            Request Resubmission
          </button>
        </div>
      </div>

      {openDocPath && (
        <div className="popup-overlay" onClick={closeModal}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={openDocPath}
              title="Document Preview"
              className="doc-frame"
            />
            <button className="btn danger close-button" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewProposal;
